import { Spinner } from '@blueprintjs/core';
import { useState } from 'react';
import { OdProtosWebData_MaintenanceRequest_State as MaintenanceRequestState } from '../../graphql/athena/types/globalTypes';
import { MaintenanceRequestFields } from '../../graphql/athena/types/MaintenanceRequestFields';
import useListMaintenanceRequests from '../../graphql/queries/web/maintenance/useListMaintenanceRequests';
import { AddMaintenanceImageIconUrl, MaintenanceImageIconUrl } from '../../icons/Icons';
import { prettifyCityStateZip, prettifyStreetAddress } from '../../utils/address';
import { stringDateToProto } from '../../utils/date';
import {
  getDueByWeekOfText,
  getFrequencyText,
  prettifyCost,
  prettifyKind,
} from '../../utils/maintenance';
import HorizontalLayout from '../layouts/HorizontalLayout';
import VerticalLayout from '../layouts/VerticalLayout';
import PageChangeButton from '../pagination/PageChangeButton';

export enum MaintenanceRequestListFilter {
  OUTSTANDING = 'Outstanding',
  PENDING_OPENDOOR_REVIEW = 'Pending Opendoor Review',
  APPROVED = 'Approved',
  DENIED = 'Denied',
}

enum MaintenanceRequestTableColumn {
  ADDRESS = 'Address',
  TYPE = 'Type',
  DUE_BY = 'Due By',
  SCHEDULED_DATE = 'Scheduled Date',
  COMPLETION_DATE = 'Completion Date',
  ON_SITE_WORKER = 'On-Site Worker',
  PAYMENT = 'Payment',
  BEFORE = 'Before',
  AFTER = 'After',
  OTHER_ACTIONS = 'Other Actions',
}

interface Column {
  id: MaintenanceRequestTableColumn;
  label: string;
  value: (request: MaintenanceRequestFields) => any;
}

const columns: Column[] = [
  {
    id: MaintenanceRequestTableColumn.ADDRESS,
    label: 'Address',
    value: (request) => {
      if (!request.address) return <div>No Address Found</div>;
      const prettyStreet = prettifyStreetAddress(request.address);
      const prettyCity = prettifyCityStateZip(request.address);

      return (
        <VerticalLayout>
          <div style={styles.streetText}>{prettyStreet}</div>
          <div style={styles.cityStateZipText}>{prettyCity}</div>
        </VerticalLayout>
      );
    },
  },
  {
    id: MaintenanceRequestTableColumn.TYPE,
    label: 'Type',
    value: (request: MaintenanceRequestFields) => {
      const kind = prettifyKind(request);
      const frequencyText = getFrequencyText(request);
      return (
        <VerticalLayout>
          <div style={styles.kindText}>{kind}</div>
          <div style={styles.frequencyText}>{frequencyText}</div>
        </VerticalLayout>
      );
    },
  },
  {
    id: MaintenanceRequestTableColumn.DUE_BY,
    label: 'Due By',
    value: (request: MaintenanceRequestFields) => {
      const dueByText = getDueByWeekOfText(request);
      return <div>{dueByText}</div>;
    },
  },
  {
    id: MaintenanceRequestTableColumn.SCHEDULED_DATE,
    label: 'Scheduled Date',
    value: (request: MaintenanceRequestFields) => {},
  },
  {
    id: MaintenanceRequestTableColumn.COMPLETION_DATE,
    label: 'Completion Date',
    value: (request: MaintenanceRequestFields) => {},
  },
  {
    id: MaintenanceRequestTableColumn.ON_SITE_WORKER,
    label: 'On-Site Worker',
    value: (request: MaintenanceRequestFields) => {},
  },
  {
    id: MaintenanceRequestTableColumn.PAYMENT,
    label: 'Payment',
    value: (request: MaintenanceRequestFields) => {
      const costText = prettifyCost(request);
      return <div>{costText}</div>;
    },
  },
  {
    id: MaintenanceRequestTableColumn.BEFORE,
    label: 'Before',
    value: (request: MaintenanceRequestFields) => {
      return <img src={AddMaintenanceImageIconUrl} alt="" />;
    },
  },
  {
    id: MaintenanceRequestTableColumn.AFTER,
    label: 'After',
    value: (request: MaintenanceRequestFields) => {
      return <img src={MaintenanceImageIconUrl} alt="" />;
    },
  },
  {
    id: MaintenanceRequestTableColumn.OTHER_ACTIONS,
    label: 'Other Actions',
    value: (request: MaintenanceRequestFields) => {},
  },
];

interface MaintenanceRequestListProps {
  organizationId: string;
  startPeriod: string;
  endPeriod: string;
  filter: MaintenanceRequestListFilter;
  pageSize?: number;
}

const MaintenanceRequestList = ({
  organizationId,
  startPeriod,
  endPeriod,
  filter,
  pageSize = 25,
}: MaintenanceRequestListProps) => {
  const [pageNumber, setPageNumber] = useState(1);

  // todo: do something with error
  const { data, loading } = useListMaintenanceRequests({
    variables: {
      orgId: organizationId,
      startPeriod: stringDateToProto(startPeriod),
      endPeriod: stringDateToProto(endPeriod),
      states: getStateParamsForFilter(filter),
      pageSize: pageSize,
      offset: (pageNumber - 1) * pageSize,
    },
  });
  const maintenanceRequests =
    data?.webMaintenance.listMaintenanceRequests?.maintenanceRequests || [];
  const totalCount = data?.webMaintenance.listMaintenanceRequests?.totalCount || 0;
  const showPageButtons = totalCount > pageSize;
  const start = pageNumber * pageSize - (pageSize - 1);
  const end = pageNumber * pageSize - (pageSize - maintenanceRequests.length);
  const onLastPage = pageNumber * pageSize >= totalCount;

  const getCurrentColumns = () => {
    const baseColumns = [
      MaintenanceRequestTableColumn.ADDRESS,
      MaintenanceRequestTableColumn.TYPE,
      MaintenanceRequestTableColumn.DUE_BY,
      MaintenanceRequestTableColumn.ON_SITE_WORKER,
      MaintenanceRequestTableColumn.PAYMENT,
      MaintenanceRequestTableColumn.BEFORE,
      MaintenanceRequestTableColumn.AFTER,
    ];
    const oustandingColumns = [
      ...baseColumns.slice(0, 3),
      MaintenanceRequestTableColumn.SCHEDULED_DATE,
      ...baseColumns.slice(3),
      MaintenanceRequestTableColumn.OTHER_ACTIONS,
    ];
    const pendingColumns = [
      ...baseColumns.slice(0, 3),
      MaintenanceRequestTableColumn.COMPLETION_DATE,
      ...baseColumns.slice(3),
      MaintenanceRequestTableColumn.OTHER_ACTIONS,
    ];
    const approvedColumns = [
      ...baseColumns.slice(0, 3),
      MaintenanceRequestTableColumn.COMPLETION_DATE,
      ...baseColumns.slice(3),
    ];
    const deniedColumns = [
      ...baseColumns.slice(0, 3),
      MaintenanceRequestTableColumn.COMPLETION_DATE,
      ...baseColumns.slice(3),
      MaintenanceRequestTableColumn.OTHER_ACTIONS,
    ];

    if (filter === MaintenanceRequestListFilter.OUTSTANDING) {
      return columns.filter((c) => oustandingColumns.includes(c.id));
    } else if (filter === MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW) {
      return columns.filter((c) => pendingColumns.includes(c.id));
    } else if (filter === MaintenanceRequestListFilter.APPROVED) {
      return columns.filter((c) => approvedColumns.includes(c.id));
    } else if (filter === MaintenanceRequestListFilter.DENIED) {
      return columns.filter((c) => deniedColumns.includes(c.id));
    } else {
      return columns.filter((c) => baseColumns.includes(c.id));
    }
  };
  const currentColumns = getCurrentColumns();

  if (loading) {
    return <Spinner />;
  }

  return (
    <VerticalLayout style={{ overflowY: 'scroll', height: '100%', width: '100%' }}>
      <table style={{ marginTop: 20, borderCollapse: 'collapse' }}>
        <thead
          style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#C7CFD7' }}
        >
          <tr>
            {currentColumns.map(({ label }, i) => {
              const headerStyle = {
                ...styles.columnHeader,
                ...(i === 0 ? { paddingLeft: 20 } : {}),
              };
              return (
                <th key={i} style={{ ...headerStyle, textAlign: 'left' }}>
                  <span style={styles.columnHeaderText}>{label}</span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {maintenanceRequests.map((request) => (
            <MaintenanceRequestRow
              key={request.id}
              request={request}
              currentColumns={currentColumns}
            />
          ))}
        </tbody>
      </table>
      {showPageButtons ? (
        <HorizontalLayout style={styles.pagination}>
          <PageChangeButton
            disabled={pageNumber === 1}
            direction="left"
            onClick={() => {
              if (pageNumber !== 1) {
                setPageNumber((currPage) => currPage - 1);
              }
            }}
          />
          <span>{`${start} - ${end}`}</span>
          <PageChangeButton
            disabled={onLastPage}
            direction="right"
            onClick={() => {
              if (!onLastPage) {
                setPageNumber((currPage) => currPage + 1);
              }
            }}
          />
        </HorizontalLayout>
      ) : null}
    </VerticalLayout>
  );
};

interface MaintenanceRequestRowProps {
  request: MaintenanceRequestFields;
  currentColumns: Column[];
}

const MaintenanceRequestRow = ({ request, currentColumns }: MaintenanceRequestRowProps) => {
  return (
    <tr style={styles.visitRow} data-testid="maintenance-request-row">
      {currentColumns.map((h, j) => {
        const tdStyle = j === 0 ? { paddingLeft: 20 } : {};
        return (
          <td key={j} style={tdStyle}>
            {h.value(request)}
          </td>
        );
      })}
    </tr>
  );
};

const getStateParamsForFilter = (currentColumn: string): MaintenanceRequestState[] => {
  switch (currentColumn) {
    case MaintenanceRequestListFilter.OUTSTANDING:
      return [MaintenanceRequestState.PENDING, MaintenanceRequestState.MISSED];
    case MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW:
      return [MaintenanceRequestState.NEEDS_REVIEW];
    case MaintenanceRequestListFilter.APPROVED:
      return [MaintenanceRequestState.APPROVED, MaintenanceRequestState.INVOICED];
    case MaintenanceRequestListFilter.DENIED:
      return [MaintenanceRequestState.DENIED];
    default:
      return [];
  }
};

export default MaintenanceRequestList;

const styles = {
  columnHeader: {
    height: 40,
  },
  columnHeaderText: {
    fontWeight: '600',
    color: '#8D9AA6',
  },
  streetText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0D76D6',
  },
  cityStateZipText: {
    fontSize: 12,
    fontWeight: '400',
    color: '#6E6E6E',
  },
  kindText: {
    fontWeight: '500',
    fontSize: 14,
    color: '#222222',
  },
  frequencyText: {
    fontWeight: '400',
    fontSize: 12,
    color: '#6E6E6E',
  },
  visitRow: {
    height: 80,
    borderBottom: '1px solid #C7CFD7',
  },
  pagination: {
    width: '100%',
    paddingTop: '2vh',
    marginBottom: '2vh',
    justifyContent: 'flex-end',
  },
};
